import { Component, DestroyRef, OnInit, Input, ViewEncapsulation, inject } from "@angular/core";
import { NgComponentOutlet } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslocoPipe, provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import { CustomFieldBase } from "../custom-field-base";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { GENERIC_LIST_MANAGER } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.token";
import { StandingDataItemWithColor } from "@models/standingdata/StandingDataItemWithColor";
import { of } from "rxjs";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";

const FRAMEWORK_ASSESSMENT_SCORES_SCOPE =
    "shared/generic-managers/generic-field-editor/custom-field-types/framework-assessment-scores";

@Component({
    selector: "framework-assessment-scores",
    templateUrl: "./framework-assessment-scores.component.html",
    styleUrls: ["./framework-assessment-scores.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [TranslocoPipe, CheckboxComponent, NgComponentOutlet],
    providers: [
        provideTranslocoScope({
            scope: FRAMEWORK_ASSESSMENT_SCORES_SCOPE,
            alias: "frameworkAssessmentScores",
        }),
    ],
})
export class FrameworkAssessmentScores extends CustomFieldBase implements OnInit {
    @Input() row: any;

    listConfig: GenericListConfig;

    private _transloco = inject(TranslocoService);
    private _destroyRef = inject(DestroyRef);
    protected readonly listManagerType = inject(GENERIC_LIST_MANAGER);

    constructor() {
        super();
    }

    ngOnInit() {
        if (!this.value) {
            this.value = [];
        }

        // The template strings go through the `transloco` pipe. The list-config strings
        // cannot: `cerrix-table.setHeaders()` copies `prettyName` into a private map once,
        // in its own `ngOnInit`, so a pipe-fed `prettyName` is still "" on that first pass
        // and the header falls back to the raw `fieldName`. So the scope is loaded here and
        // those keys alone are resolved against the scope-qualified lang.
        const scopeLang = `${FRAMEWORK_ASSESSMENT_SCORES_SCOPE}/${this._transloco.getActiveLang()}`;
        this._transloco
            .load(scopeLang)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                const t = (key: string) => this._transloco.translate<string>(key, {}, scopeLang);

                this.initListConfig(t);
                this.useParentOrDefaultChecked(this.row.UseParentOrDefaultScores);
            });
    }

    useParentOrDefaultChecked(val: boolean) {
        this.row.UseParentOrDefaultScores = val;
        if (!this.listConfig) {
            // The checkbox paints before the scope load resolves. `ngOnInit` re-applies the
            // current value once the config exists, so dropping this early click is safe.
            return;
        }

        if (val) {
            this.setListConfig(this.row.ParentScores);
        } else {
            this.setListConfig(this.row.Scores);
        }
    }

    private initListConfig(t: (key: string) => string) {
        const allowEdit = !this.row.UseParentOrDefaultScores;

        this.listConfig = <GenericListConfig>{
            name: t("listName"),
            isSortable: allowEdit,

            allowAdd: allowEdit,
            allowEdit: allowEdit,
            allowDelete: allowEdit,
            idProp: "ID",
            sortProp: "Sort_Order",

            fields: [
                {
                    prettyName: t("nameColumn"),
                    fieldName: "Name",
                    fieldType: GenericListFieldType.Text,
                    required: true,
                    unique: true,
                },
                {
                    prettyName: t("colorColumn"),
                    fieldName: "Color",
                    fieldType: GenericListFieldType.ColorPicker,
                    required: true,
                    maxLength: 128,
                },
                {
                    prettyName: t("weightColumn"),
                    fieldName: "Weight",
                    fieldType: GenericListFieldType.Number,
                    required: false,
                    maxLength: 128,
                    defaultValue: 0,
                },
            ],

            dataChanged: (data) => {
                if (this.row.UseParentOrDefaultScores) {
                    this.row.Scores = data;
                }
            },
        };
    }

    private setListConfig(data: StandingDataItemWithColor[]) {
        const allowEdit = !this.row.UseParentOrDefaultScores;

        this.listConfig.isSortable = allowEdit;
        this.listConfig.allowAdd = allowEdit;
        this.listConfig.allowEdit = allowEdit;
        this.listConfig.allowDelete = allowEdit;

        this.listConfig.dataMethod = () => {
            return of(data);
        };

        this.listConfig.reload();
    }
}
